import { validatedDisplayView, extendDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `resource-share` of the console gateway — over `apps/api/src/routes/resource-share/` (design §27.9.2). Owned by
 * plan-014 unit **B5**.
 *
 *   readResourceSharing     `listAccountResourceShares`   the Sharing page: shares, what I could share, the combined list
 *   shareResource           `createResourceShare`         a source workspace publishes a revision
 *   acceptResourceShare     `acceptResourceShare`         a destination workspace accepts it
 *   revokeResourceShare     `revokeResourceShare`         the source (or the organization) revokes; the answer names who
 *                                                         keeps the revision in what is already built
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in B5's fragments; the merged spec and `@wetalk/api-client`'s generated client are regenerated by
 * the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through {@link operationsOf},
 * a structural view of the client with exactly these methods (C1's, B2's and C2's precedent): it compiles before and
 * after the regeneration, and a client that still lacks one raises naming the regeneration — never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export type ResourceShareKindView = "agent_document" | "voice_agent_configuration";
export type ResourceShareStateView = "published" | "accepted" | "revoked";
export type ResourceShareModeView = "reference" | "copy";
export type ResourceShareOutcomeKindView = "published" | "already_published" | "accepted" | "already_accepted" | "revoked" | "already_revoked";

export interface ResourceShareWorkspaceView {
  readonly workspaceId: string;
  readonly name: string;
}

/** One VoiceAgent version that carries a shared revision in the receiving workspace. */
export interface ResourceShareUseView {
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  readonly agentVersionLabel: string | null;
  readonly isLiveVersion: boolean;
}

export interface ResourceShareConsumerView {
  readonly resourceShareRevisionRefId: string;
  readonly workspaceId: string;
  readonly name: string;
  readonly mode: ResourceShareModeView;
  readonly copiedResourceId: string | null;
  readonly acceptedAt: string;
  readonly inUse: readonly ResourceShareUseView[];
}

export interface ResourceShareView {
  readonly resourceShareId: string;
  readonly resourceKind: ResourceShareKindView;
  readonly resourceId: string;
  readonly label: string | null;
  readonly revisionOrdinal: number | null;
  readonly state: ResourceShareStateView;
  readonly sourceWorkspace: ResourceShareWorkspaceView;
  /** `null`: offered to every other workspace of the organization. */
  readonly destinationWorkspace: ResourceShareWorkspaceView | null;
  readonly publishedAt: string;
  readonly acceptedAt: string | null;
  readonly revokedAt: string | null;
  readonly consumer: readonly ResourceShareConsumerView[];
}

export interface ResourceShareOutcomeView extends ResourceShareView {
  readonly outcome: ResourceShareOutcomeKindView;
}

export interface ShareableResourceView {
  readonly workspaceId: string;
  readonly resourceKind: ResourceShareKindView;
  readonly resourceId: string;
  readonly label: string;
  readonly voiceAgentName: string;
  readonly revisionOrdinal: number;
}

export interface ResourceSharingView {
  /** Whether every share of the organization is shown (an organization Owner or Admin). */
  readonly accountWide: boolean;
  readonly share: readonly ResourceShareView[];
  readonly shareable: readonly ShareableResourceView[];
  readonly publishableWorkspaceId: readonly string[];
  readonly acceptableWorkspaceId: readonly string[];
  readonly workspace: readonly (ResourceShareWorkspaceView & { readonly state: "active" | "draining" | "archived" })[];
  /** The always-combined do-not-call list's size. */
  readonly doNotCallEntryCount: number;
}

export interface ShareResourceInput {
  readonly resourceKind: ResourceShareKindView;
  readonly resourceId: string;
  /** `null` offers it to every other workspace. */
  readonly destinationWorkspaceId: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** What is shared between the organization's workspaces, what I could share, and the combined do-not-call list. */
    readResourceSharing(accountId: string): Promise<ResourceSharingView>;
    /** Publishes a document revision or an agent's configuration from a workspace (`resource_share.publish` there). */
    shareResource(sourceWorkspaceId: string, input: ShareResourceInput): Promise<ResourceShareOutcomeView>;
    /** Accepts for a workspace (`consumerWorkspaceId`, else the share's destination). */
    acceptResourceShare(resourceShareId: string, consumerWorkspaceId: string | null): Promise<ResourceShareOutcomeView>;
    /** Revokes: nothing new can use it; what is already built keeps it (the answer names who). */
    revokeResourceShare(resourceShareId: string): Promise<ResourceShareOutcomeView>;
  }
}

export type ResourceShareGatewaySlice = Pick<ConsoleGateway, "readResourceSharing" | "shareResource" | "acceptResourceShare" | "revokeResourceShare">;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in B5's fragments. */
export const resourceShareGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

interface ResourceShareOperations {
  listAccountResourceShares(request: { path: { readonly account_id: string } }): Promise<ApiResult>;
  createResourceShare(request: { path: { readonly workspace_id: string }; body?: unknown }): Promise<ApiResult>;
  acceptResourceShare(request: { path: { readonly resource_share_id: string }; body?: unknown }): Promise<ApiResult>;
  revokeResourceShare(request: { path: { readonly resource_share_id: string } }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): ResourceShareOperations {
  const held = client as unknown as Partial<Record<keyof ResourceShareOperations, unknown>>;
  return new Proxy({} as ResourceShareOperations, {
    get(_target, name: string) {
      const found = held[name as keyof ResourceShareOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_37a3e937", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

const KIND: readonly ResourceShareKindView[] = ["agent_document", "voice_agent_configuration"];
const STATE: readonly ResourceShareStateView[] = ["published", "accepted", "revoked"];
const MODE: readonly ResourceShareModeView[] = ["reference", "copy"];
const OUTCOME: readonly ResourceShareOutcomeKindView[] = ["published", "already_published", "accepted", "already_accepted", "revoked", "already_revoked"];
const WORKSPACE_STATE = ["active", "draining", "archived"] as const;

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const text = readText(value, path);
  const found = allowed.find((one) => one === text);
  if (found === undefined) throw new Error(`${path}: "${text}" is not one of ${allowed.join(", ")}`);
  return found;
}

function workspaceRefOf(value: unknown, path: string): ResourceShareWorkspaceView {
  const row = readRecord(value, path);
  return { workspaceId: readText(row["workspace_id"], `${path}.workspace_id`), name: readText(row["name"], `${path}.name`) };
}

export function resourceShareOf(value: unknown, path: string): ResourceShareView {
  const row = readRecord(value, path);
  const destination = row["destination_workspace"];
  const ordinal = row["revision_ordinal"];
  return {
    resourceShareId: readText(row["resource_share_id"], `${path}.resource_share_id`),
    resourceKind: oneOf(KIND, row["resource_kind"], `${path}.resource_kind`),
    resourceId: readText(row["resource_id"], `${path}.resource_id`),
    label: textOrNull(row["label"], `${path}.label`),
    revisionOrdinal: ordinal === null || ordinal === undefined ? null : readWholeNumber(ordinal, `${path}.revision_ordinal`),
    state: oneOf(STATE, row["state"], `${path}.state`),
    sourceWorkspace: workspaceRefOf(row["source_workspace"], `${path}.source_workspace`),
    destinationWorkspace: destination === null || destination === undefined ? null : workspaceRefOf(destination, `${path}.destination_workspace`),
    publishedAt: readText(row["published_at"], `${path}.published_at`),
    acceptedAt: textOrNull(row["accepted_at"], `${path}.accepted_at`),
    revokedAt: textOrNull(row["revoked_at"], `${path}.revoked_at`),
    consumer: readArray(row["consumer"], `${path}.consumer`).map((one, index) => {
      const at = `${path}.consumer[${index}]`;
      const consumer = readRecord(one, at);
      return {
        resourceShareRevisionRefId: readText(consumer["resource_share_revision_ref_id"], `${at}.resource_share_revision_ref_id`),
        workspaceId: readText(consumer["workspace_id"], `${at}.workspace_id`),
        name: readText(consumer["name"], `${at}.name`),
        mode: oneOf(MODE, consumer["mode"], `${at}.mode`),
        copiedResourceId: textOrNull(consumer["copied_resource_id"], `${at}.copied_resource_id`),
        acceptedAt: readText(consumer["accepted_at"], `${at}.accepted_at`),
        inUse: readArray(consumer["in_use"], `${at}.in_use`).map((use, useIndex) => {
          const usePath = `${at}.in_use[${useIndex}]`;
          const record = readRecord(use, usePath);
          return {
            voiceAgentId: readText(record["voice_agent_id"], `${usePath}.voice_agent_id`),
            voiceAgentName: readText(record["voice_agent_name"], `${usePath}.voice_agent_name`),
            agentVersionLabel: textOrNull(record["agent_version_label"], `${usePath}.agent_version_label`),
            isLiveVersion: readBoolean(record["is_live_version"], `${usePath}.is_live_version`),
          };
        }),
      };
    }),
  };
}

function outcomeOf(data: Readonly<Record<string, unknown>>, operationId: string): ResourceShareOutcomeView {
  return extendDisplayView(resourceShareOf(data, operationId), { outcome: oneOf(OUTCOME, data["outcome"], `${operationId}.outcome`) });
}

export function resourceSharingOf(data: Readonly<Record<string, unknown>>): ResourceSharingView {
  const doNotCall = readRecord(data["do_not_call"], "do_not_call");
  return {
    accountWide: readBoolean(data["account_wide"], "account_wide"),
    share: readArray(data["resource_share"], "resource_share").map((one, index) => resourceShareOf(one, `resource_share[${index}]`)),
    shareable: readArray(data["shareable"], "shareable").map((one, index) => {
      const path = `shareable[${index}]`;
      const row = readRecord(one, path);
      return validatedDisplayView({
        workspaceId: readText(row["workspace_id"], `${path}.workspace_id`),
        resourceKind: oneOf(KIND, row["resource_kind"], `${path}.resource_kind`),
        resourceId: readText(row["resource_id"], `${path}.resource_id`),
        get label() { return readText(row["label"], `${path}.label`); },
        voiceAgentName: readText(row["voice_agent_name"], `${path}.voice_agent_name`),
        revisionOrdinal: readWholeNumber(row["revision_ordinal"], `${path}.revision_ordinal`),
      });
    }),
    publishableWorkspaceId: readArray(data["publishable_workspace_id"], "publishable_workspace_id").map((one) => readText(one, "publishable_workspace_id")),
    acceptableWorkspaceId: readArray(data["acceptable_workspace_id"], "acceptable_workspace_id").map((one) => readText(one, "acceptable_workspace_id")),
    workspace: readArray(data["workspace"], "workspace").map((one, index) => {
      const path = `workspace[${index}]`;
      const row = readRecord(one, path);
      return extendDisplayView(workspaceRefOf(row, path), { state: oneOf(WORKSPACE_STATE, row["state"], `${path}.state`) });
    }),
    doNotCallEntryCount: readWholeNumber(doNotCall["active_entry_count"], "do_not_call.active_entry_count"),
  };
}

// ── the slice ─────────────────────────────────────────────────────────────────────────────────────────────

export function resourceShareApiSlice(client: WeTalkApiClient, _context: SliceContext): ResourceShareGatewaySlice {
  const operation = operationsOf(client);
  return {
    async readResourceSharing(accountId) {
      return resourceSharingOf(dataOf(await operation.listAccountResourceShares({ path: { account_id: accountId } }), "listAccountResourceShares"));
    },
    async shareResource(sourceWorkspaceId, input) {
      const result = await operation.createResourceShare({
        path: { workspace_id: sourceWorkspaceId },
        body: { resource_kind: input.resourceKind, resource_id: input.resourceId, destination_workspace_id: input.destinationWorkspaceId },
      });
      return outcomeOf(dataOf(result, "createResourceShare"), "createResourceShare");
    },
    async acceptResourceShare(resourceShareId, consumerWorkspaceId) {
      const result = await operation.acceptResourceShare({
        path: { resource_share_id: resourceShareId },
        body: consumerWorkspaceId === null ? {} : { consumer_workspace_id: consumerWorkspaceId },
      });
      return outcomeOf(dataOf(result, "acceptResourceShare"), "acceptResourceShare");
    },
    async revokeResourceShare(resourceShareId) {
      return outcomeOf(dataOf(await operation.revokeResourceShare({ path: { resource_share_id: resourceShareId } }), "revokeResourceShare"), "revokeResourceShare");
    },
  };
}
